import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { storeRef } from "@/lib/referral";
import { Building2, Hotel, Lock, KeyRound, BadgeCheck, ArrowRight, Globe2, ShieldCheck, Handshake, ExternalLink } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1512100356356-de1b84283e18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxwcml2YXRlJTIwdHJhdmVsJTIwY2x1YnxlbnwwfHx8fDE3ODM2NDMyMjF8MA&ixlib=rb-4.1.0&q=85";
const HOTELS_IMG = "https://images.unsplash.com/photo-1610641818989-c2051b5e2cfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjByZXNvcnQlMjBwb29sfGVufDB8fHx8MTc4MzY0MzIyMXww&ixlib=rb-4.1.0&q=85";
const CONDOS_IMG = "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb25kbyUyMGludGVyaW9yfGVufDB8fHx8MTc4MzY0MzIyMnww&ixlib=rb-4.1.0&q=85";
const HOTELS_URL = "https://hotels.internationalvacations.com/v6/";
const CONDOS_URL = "https://www.condobooker.com/booknow.aspx";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const Landing = () => {
  const [commission, setCommission] = useState({ percent: 30, per_referral_amount: 30 });

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      storeRef(ref);
      api.post("/referral/click", { code: ref }).catch(() => {});
    }
    api.get("/public/commission").then(({ data }) => setCommission(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (window.location.hash === "#affiliate") {
      setTimeout(() => document.getElementById("affiliate")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  const pct = commission.percent;
  const amt = commission.per_referral_amount;
  const commissionLabel = amt > 0 ? `${pct}% ($${amt})` : `${pct}%`;

  return (
    <div className="relative min-h-screen bg-[var(--ir-bg)] text-[var(--ir-text)] overflow-x-hidden" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="Private island seaplane" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-24">
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="inline-flex items-center gap-2 px-4 py-1.5 border border-[var(--ir-orange)]/40 rounded-full mb-8 bg-white/70">
            <Lock className="w-3.5 h-3.5 text-[var(--ir-orange)]" />
            <span className="text-xs tracking-[0.25em] uppercase text-[var(--ir-orange)] font-semibold">Members Access Only</span>
          </motion.div>

          <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="show" className="font-display font-light text-5xl sm:text-7xl lg:text-8xl leading-[0.95] max-w-4xl tracking-tight text-[var(--ir-text)]">
            The rates the rest of the<br />
            <span className="text-gradient-orange italic font-medium">world never sees.</span>
          </motion.h1>

          <motion.p variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-8 text-lg text-[var(--ir-muted)] max-w-xl leading-relaxed">
            Image Reservations is a private travel club unlocking deep, hidden discounts on luxury hotels and vacation condos worldwide — reserved exclusively for approved members. Our member pricing is private and never shown to the general public.
          </motion.p>

          <motion.div variants={fadeUp} custom={3} initial="hidden" animate="show" className="mt-10 flex flex-wrap items-center gap-4">
            <Button asChild className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none px-8 h-12 text-base soft-shadow" data-testid="hero-join-btn">
              <Link to="/register">Request Membership <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild variant="outline" className="border-[var(--ir-blue)]/30 bg-white/70 text-[var(--ir-blue)] hover:bg-white rounded-none px-8 h-12 text-base" data-testid="hero-login-btn">
              <Link to="/login">I'm a Member</Link>
            </Button>
          </motion.div>

          <motion.div variants={fadeUp} custom={4} initial="hidden" animate="show" className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-sm text-[var(--ir-muted)]">
            <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[var(--ir-orange)]" /> Up to 35% off condos</span>
            <span className="flex items-center gap-2"><Globe2 className="w-4 h-4 text-[var(--ir-orange)]" /> Global inventory</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--ir-orange)]" /> Vetted members only</span>
            <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-[var(--ir-orange)]" /> Prices are not open to the public</span>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-28">
        <div className="max-w-2xl mb-16">
          <span className="text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">Two Ways To Escape</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-[var(--ir-text)]">Members-only access to deep discounts</h2>
          <p className="text-[var(--ir-muted)] mt-5">Every member receives a private 10-digit PIN number to reveal live, members-only inventory and pricing.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {[
            { title: "Hotels & Resorts", img: HOTELS_IMG, icon: Hotel, url: HOTELS_URL, desc: "Select from 800,000 hotels worldwide — 4 and 5-star properties with savings of up to 35% off published rates.", note: "These rates are not available to the general public — members only." },
            { title: "Vacation Condos", img: CONDOS_IMG, icon: Building2, url: CONDOS_URL, desc: "Spacious condos and last-minute Hot Weeks — save up to 35% at the time of booking.", note: "These rates are not available to the general public — members only." },
          ].map((c, i) => (
            <motion.a
              key={c.title}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="group relative block overflow-hidden border border-[var(--ir-border)] bg-white rounded-lg soft-shadow transition-transform duration-300 hover:-translate-y-1.5 cursor-pointer"
              data-testid={`landing-category-${c.title.includes("Hotel") ? "hotels" : "condos"}`}
            >
              <div className="relative h-72 overflow-hidden">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-3">
                  <c.icon className="w-6 h-6 text-[var(--ir-blue)]" />
                  <h3 className="font-display text-3xl text-[var(--ir-text)]">{c.title}</h3>
                </div>
                <p className="text-[var(--ir-muted)] leading-relaxed">{c.desc}</p>
                <p className="text-[var(--ir-text)] text-sm font-medium mt-3">{c.note}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-[var(--ir-orange)] font-medium">
                    <KeyRound className="w-4 h-4" /> Exclusive members-only rates
                  </span>
                  <span className="flex items-center gap-1.5 text-sm text-[var(--ir-blue)] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Open booking portal <ExternalLink className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative z-10 border-y border-[var(--ir-border)] bg-[var(--ir-surface2)]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="font-display text-4xl sm:text-5xl mb-16 max-w-xl text-[var(--ir-text)]">How membership works</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { n: "01", t: "Request access", d: "Apply in under a minute. Tell us who you are and how to reach you." },
              { n: "02", t: "Get approved", d: "Our team reviews and approves your membership, then issues your unique 10-digit PIN number." },
              { n: "03", t: "Book privately", d: "Enter your PIN number on the Hotels or Condos portal and book at hidden member rates." },
            ].map((s, i) => (
              <motion.div key={s.n} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="border-l-2 border-[var(--ir-orange)] pl-6">
                <div className="font-display text-5xl text-gradient-blue font-semibold">{s.n}</div>
                <h3 className="text-xl font-semibold mt-4 text-[var(--ir-text)]">{s.t}</h3>
                <p className="text-[var(--ir-muted)] mt-2 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO MAKE A RESERVATION */}
      <section id="how-to-reserve" className="relative z-10 max-w-7xl mx-auto px-6 py-28 scroll-mt-28" data-testid="how-to-reserve">
        <div className="max-w-2xl mb-14">
          <span className="text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">Hotels &amp; Resorts</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-[var(--ir-text)]">How to make a reservation</h2>
          <p className="text-[var(--ir-muted)] mt-5">Book your 4 and 5-star hotel in a few simple steps using your private PIN number.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
          {[
            "Enter your PIN number to unlock the booking portal.",
            "Input city, check-in date, nights, and number of persons (children are under 18).",
            "Enter the hotel name or click \u201CGO\u201D for a list, and choose your hotel.",
            "Click \u201CVIEW RATES\u201D for the base rate, excluding taxes and fees.",
            "Click \u201CROOM DETAILS,\u201D select the room type, and review the price.",
            "Compare your subscription price with other travel sites.",
            "Complete reservation details and confirm — prepayment is required.",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-4" data-testid={`reserve-step-${i + 1}`}>
              <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-[var(--ir-blue)] text-white font-display text-lg font-semibold">{i + 1}</div>
              <p className="text-[var(--ir-text)] leading-relaxed pt-1.5">{step}</p>
            </div>
          ))}
        </div>

        {/* RATE-MATCH GUARANTEE */}
        <div className="mt-16 rounded-xl border border-[var(--ir-orange)]/30 bg-[var(--ir-orange)]/5 p-8 sm:p-10 flex flex-col sm:flex-row items-start gap-6" data-testid="rate-match-guarantee">
          <div className="shrink-0 w-14 h-14 flex items-center justify-center rounded-full bg-[var(--ir-orange)] text-white">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <h3 className="font-display text-2xl sm:text-3xl text-[var(--ir-text)]">Rate-match guarantee</h3>
            <p className="text-[var(--ir-muted)] mt-3 leading-relaxed max-w-3xl">
              Find a better price elsewhere? Contact us within 24 hours and we'll match or beat the lowest rate — plus send you a cashback check and a travel gift card.
            </p>
          </div>
        </div>
      </section>

      {/* AFFILIATE */}
      <section id="affiliate" className="relative z-10 overflow-hidden bg-[var(--ir-blue)] scroll-mt-28" data-testid="landing-affiliate">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--ir-blue)] via-[var(--ir-blue)] to-[#0b2a4a]" />
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">
              <Handshake className="w-4 h-4" /> Affiliate Program
            </span>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight mt-5 text-white">
              Earn <span className="text-gradient-orange italic" data-testid="affiliate-commission-headline">{commissionLabel} commission</span> on every member you refer.
            </h2>
            <p className="mt-6 text-lg text-white/85 leading-relaxed">
              Share your personal referral link. When someone signs up through it and is approved as a member, you earn <strong className="text-white">{commissionLabel}</strong> per approved member sign-up — credited straight to your affiliate balance. Track your clicks, referrals and earnings from your own dashboard.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button asChild className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none px-8 h-12 text-base" data-testid="affiliate-cta-btn">
                <Link to="/register">Apply to Join <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-white/40 bg-white/10 text-white hover:bg-white/20 rounded-none px-8 h-12 text-base" data-testid="affiliate-login-btn">
                <Link to="/login">Affiliate Login</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 bg-white border-t border-[var(--ir-border)]">
        <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col md:flex-row items-start md:justify-between gap-8">
          <div>
            <Logo size="md" />
            <p className="text-[var(--ir-muted)] text-sm mt-4 max-w-sm">A private travel club. Deep discounts, discreetly delivered.</p>
          </div>
          <div className="flex flex-col gap-3 text-sm">
            <span className="text-xs tracking-[0.25em] uppercase text-[var(--ir-orange)] font-semibold">Learn more</span>
            <a href="#how-to-reserve" className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors" data-testid="footer-how-to-link">How to Make a Reservation</a>
            <Link to="/terms" className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors" data-testid="footer-terms-link">Terms &amp; Card Usage</Link>
            <a href="#affiliate" className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors" data-testid="footer-affiliate-link">Affiliate Program</a>
          </div>
          <div className="text-[var(--ir-muted)] text-sm">© {new Date().getFullYear()} Image Reservations. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
